// /mixtapes — the Top 100 mixtapes overview: every finished day as a tile (its date, genre, #1
// song and how many songs it has); click one to open its tape. Tiles warm up from a saturated
// yellow towards red the more often that day's tape has been played.
import { useEffect, useState } from "react";
import { api } from "../api";
import { Link, useTitle } from "../lib/router";

interface MixtapeDay {
  day: number;
  date: string;
  genre: string | null;
  songs: number;
  top: { loopId: string; title: string | null; author: string | null; likes: number } | null;
  plays: number;
}

/** Heat colour for a share 0–1 of the most played tape: saturated yellow → orange → red. */
export function heat(share: number): { bg: string; fg: string } {
  const t = Math.max(0, Math.min(1, share));
  const hue = 46 - 46 * t; // 46° yellow … 0° red
  const sat = 88 - 8 * t;
  const light = 52 - 6 * t;
  return { bg: `hsl(${hue.toFixed(0)} ${sat}% ${light}%)`, fg: t > 0.55 ? "#fff8f2" : "#1d1405" };
}

export function Mixtapes() {
  useTitle("Top100 Mixtapes");
  const [days, setDays] = useState<MixtapeDay[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // The first page replaces the list (effects may run twice in development); "older" appends.
  const load = (to?: number, isAlive: () => boolean = () => true) => {
    setLoading(true);
    api<{ days: MixtapeDay[]; more: boolean }>("GET", `/api/mixtapes?count=42${to !== undefined ? `&to=${to}` : ""}`)
      .then((r) => {
        if (!isAlive()) return;
        setDays((d) => (to === undefined ? r.days : [...d, ...r.days.filter((x) => !d.some((y) => y.day === x.day))]));
        setMore(r.more);
      })
      .catch((e) => isAlive() && setError(String(e)))
      .finally(() => isAlive() && setLoading(false));
  };
  useEffect(() => {
    let alive = true;
    load(undefined, () => alive);
    return () => {
      alive = false;
    };
  }, []);

  const max = Math.max(0, ...days.map((d) => d.plays));
  const share = (plays: number) => (max > 0 ? Math.log1p(plays) / Math.log1p(max) : 0);

  return (
    <main id="main" className="page page-wide mixtapes-page">
      <h1>Top100 Mixtapes</h1>
      <p className="muted">
        Every finished day becomes a tape: its 100 most liked songs, counting down to #1. The more a tape is played, the
        redder it glows.
      </p>
      {error && <p role="alert">{error}</p>}
      {!loading && days.length === 0 && <p className="muted">The first tape appears once the first day is over.</p>}
      <ol className="mixtape-grid" data-testid="mixtape-grid">
        {days.map((d) => {
          const c = heat(share(d.plays));
          const label = new Date(`${d.date}T12:00:00Z`).toLocaleDateString(undefined, {
            weekday: "short",
            day: "numeric",
            month: "short",
          });
          return (
            <li key={d.day}>
              <Link
                href={`/mixtapes/${d.day}`}
                className={`mixtape-tile${d.songs === 0 ? " empty" : ""}`}
                style={d.songs ? { background: c.bg, color: c.fg } : undefined}
                data-testid={`mixtape-${d.day}`}
                data-plays={d.plays}
              >
                <span className="mixtape-date">{label}</span>
                {d.genre && <span className="mixtape-genre">{d.genre}</span>}
                {d.top ? (
                  <span className="mixtape-top">
                    #1 <strong>{d.top.title ?? "Untitled song"}</strong> · {d.top.author ?? "former contributor"}
                  </span>
                ) : (
                  <span className="mixtape-top">No liked songs</span>
                )}
                <span className="mixtape-meta">
                  {d.songs} {d.songs === 1 ? "song" : "songs"} · ▶ {d.plays}
                </span>
              </Link>
            </li>
          );
        })}
      </ol>
      {more && (
        <p className="mixtape-more">
          <button
            type="button"
            className="btn"
            disabled={loading}
            onClick={() => load(days[days.length - 1].day - 1)}
            data-testid="mixtape-older"
          >
            Show older days
          </button>
        </p>
      )}
    </main>
  );
}
